<template>
  <v-card v-if="paymentPlan !== null">
    <v-toolbar flat light color="white" v-if="$vuetify.breakpoint.smAndDown">
      <v-toolbar-title>{{ $t('pps-text-1') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn light icon @click="$emit('close')">
        <v-icon>fa-times</v-icon>
      </v-btn>
    </v-toolbar>
    <v-divider v-if="$vuetify.breakpoint.smAndDown"></v-divider>
    <v-container class="pb-0" v-if="$vuetify.breakpoint.mdAndUp">
      <v-row justify="end">
        <v-col class="shrink">
          <v-btn color="color-grey" depressed right text small @click="$emit('close')">
            <v-icon class="mr-3">fa-times</v-icon>
            <span>{{ $t('close') }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="pt-0 pb-16" fluid>
      <v-row justify="center" v-if="$vuetify.breakpoint.mdAndUp">
        <v-col class="text-center">
          <h3 class="mt-4 text-h3">{{ $t('pps-text-1') }}</h3>
        </v-col>
      </v-row>
      <v-row justify="center" :class="{'text-center' : $vuetify.breakpoint.mdAndUp }">
        <v-col cols="12" sm="10">
          <p>{{ $t('pps-text-2') }}</p>
          <p>{{ $t('pps-text-3') }}</p>
        </v-col>
      </v-row>
      <v-row justify="center">
        <div class="pa-3">
          <p class="ok-title">{{ $t('pps-text-7') }}</p>
          <p class="ok-subtitle">{{ installments[0].duedate | formatDate }}</p>
        </div>
        <div class="pa-3">
          <p class="ok-title">{{ $t('amountToPay') }}</p>
          <p class="ok-subtitle font-weight-bold">{{ installments[0].eur | currency }}</p>
        </div>
        <div class="pa-3">
          <p class="ok-title">{{ $t('pps-text-9') }}</p>
          <p class="ok-subtitle">{{ installments.length }} {{ $t('kpl') }}</p>
        </div>

      </v-row>
      <v-row class="mb-3">
        <v-col cols="12">
          <v-divider></v-divider>
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="pp-expansion-header py-6 text-center" expand-icon="fas fa-chevron-circle-down">
                <span class="ok-title mb-0">{{ $t('pps-text-6') }}</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table :headers="headers"
                              :items="installments"
                              hide-default-footer
                              :items-per-page="-1"
                              :mobile-breakpoint="0">
                  <template #item.sequence="{ item }">
                    <v-badge color="white">
                      <span slot="badge" class="black--text">{{ item.sequence }}</span>
                    </v-badge>
                  </template>
                  <template #item.duedate="{ item }">
                    {{ item.duedate | formatDate }}
                  </template>
                  <template #item.amount="{ item }">
                    {{ item.eur | currency }}
                  </template>
                  <template #item.capital="{ item }">
                    {{ item.capital | currency }}
                  </template>
                  <template #item.costs="{ item }">
                    {{ item.costs | currency }}
                  </template>
                  <template #item.interest="{ item }">
                    {{ item.interest | currency }}
                  </template>
                </v-data-table>
                <v-row justify="center">
                  <div class="pa-3">
                    <span class="ok-title small mr-2">{{ $t('capital') }}:</span>
                    <span class="text-caption">{{ summary.total | currency }}</span>
                  </div>
                  <div class="pa-3">
                    <span class="ok-title small mr-2">{{ $t('interests') }}:</span>
                    <span class="text-caption">{{ summary.interest | currency }}</span>
                  </div>
                  <div class="pa-3">
                    <span class="ok-title small mr-2">{{ $t('costs') }}:</span>
                    <span class="text-caption">{{ summary.cost | currency }}</span>
                  </div>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-divider></v-divider>
        </v-col>
      </v-row>
      <v-row justify="center">
        <div class="pa-3">
          <v-btn tile color="primary" class="wide-button" :disabled="loading" :loading="loading" @click="confirmChoice(true)">
            {{ $t('accept') }}
          </v-btn>
        </div>
        <div class="pa-3">
          <v-btn tile color="color-red" class="wide-button white--text" :disabled="loading" @click="declineWarningDialog = true">
            {{ $t('decline') }}
          </v-btn>
        </div>
      </v-row>
    </v-container>
    <v-dialog v-model="declineWarningDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <v-card>
        <v-toolbar flat light color="white" v-if="$vuetify.breakpoint.smAndDown">
          <v-toolbar-title>{{ $t('pps-text-4') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn light icon @click="declineWarningDialog = false">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider v-if="$vuetify.breakpoint.smAndDown"></v-divider>
        <v-container class="pt-0 pb-16" fluid>
          <v-row justify="center" v-if="$vuetify.breakpoint.mdAndUp">
            <v-col class="text-center">
              <h3 class="mt-4 text-h3">{{ $t('pps-text-4') }}</h3>
            </v-col>
          </v-row>
          <v-row justify="center" class="text-center">
            <v-col cols="12" sm="10">
              <p>{{ $t('pps-text-5') }}</p>
            </v-col>
          </v-row>
          <v-row justify="center">
            <div class="pa-3">
              <v-btn tile color="color-red" class="wide-button white--text" :disabled="loading" :loading="loading" @click="confirmChoice(false)">
                {{ $t('decline') }}
              </v-btn>
            </div>
            <div class="pa-3">
              <v-btn tile outlined color="color-grey" class="wide-button" @click="declineWarningDialog = false">
                {{ $t('back') }}
              </v-btn>
            </div>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  export default {
    props: ['paymentPlan'],
    data: function () {
      return {
        declineWarningDialog: false
      }
    },
    computed: {
      installments() {
        return this.paymentPlan !== null ? this.paymentPlan.installments : null
      },
      summary() {
        return this.paymentPlan !== null ? this.paymentPlan.summary : null
      },
      headers() {
        let desktopHeaders = [
          { text: this.$t('installment'), value: 'sequence', sortable: false, class: 'pp-table-header' },
          { text: this.$t('dueDate'), value: 'duedate', sortable: false, class: 'pp-table-header' },
          { text: this.$t('installmentSequence'), value: 'amount', sortable: false, class: 'pp-table-header' },
          { text: this.$t('capital'), value: 'capital', sortable: false, class: 'pp-table-header' },
          { text: this.$t('costs'), value: 'costs', sortable: false, class: 'pp-table-header' },
          { text: this.$t('interests'), value: 'interest', sortable: false, class: 'pp-table-header' }
        ]

        let mobileHeaders = [
          { text: this.$t('installment'), value: 'sequence', sortable: false, class: 'pp-table-header' },
          { text: this.$t('dueDate'), value: 'duedate', sortable: false, class: 'pp-table-header' },
          { text: this.$t('installmentSequence'), value: 'amount', sortable: false, class: 'pp-table-header' }
        ]

        return this.$vuetify.breakpoint.smAndDown ? mobileHeaders : desktopHeaders
      },
      loading() {
        return this.$store.getters.loading
      },
      dialogSettings() {
        return {
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: false,
          hideoverlay: this.$vuetify.breakpoint.smAndDown,
          transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
          width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '1024px',
          maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
        }
      }
    },
    methods: {
      confirmChoice(val) {
        var data = {
          orderNr: this.paymentPlan.debts[0].nr,
          msNr: this.paymentPlan.nr,
          accept: val
        }

        this.$store.dispatch('confirmSuggestedPaymentPlan', data).then(message => {
          if (val) {
            this.$store.commit('setMessage', { type: 'primary', text: 'confirmPaymentPlan.1', translate: true })
            this.$store.commit('addPaymentPlan', this.paymentPlan.nr)
            this.$emit('showPP')
          } else {
            this.$store.commit('setMessage', { type: 'primary', text: 'confirmPaymentPlan.2', translate: true })
            this.$store.commit('removePaymentPlan', this.paymentPlan.nr)
          }
          this.$emit('close')
        }, error => {
          console.log(error)
          this.$store.commit('setMessage', { type: 'error', text: 'confirmPaymentPlan.0', clickToDismiss: true, translate: true })
        })
      },
    }
  }
</script>

<i18n>
  {
  "fi": {
  "pps-text-1": "Maksusuunnitelmaehdotus",
  "pps-text-2": "Olemme laatineet sinulle maksusuunnitelmaehdotuksen, jonka avulla voit maksaa velkasi useammassa erässä sovitun aikataulun mukaan. Alla näet suunnitelman tiedot.",
  "pps-text-3": "Jos haluat maksaa velan tämän ehdotuksen mukaan, hyväksy ehdotus. Tällöin maksusuunnitelmasi tulee voimaan automaattisesti.",
  "pps-text-4": "Haluatko varmasti hylätä ehdotuksen?",
  "pps-text-5": "Jos hylkäät maksusuunnitelmaehdotuksen, se purkautuu välittömästi eikä sitä saa voimaan uudelleen. Velkasi perintä jatkuu normaalisti.",
  "pps-text-6": "Näytä suunnitelman tarkemmat tiedot",
  "pps-text-7": "Ensimmäinen erä",
  "pps-text-9": "Eriä"
  },
  "se": {
  "pps-text-1": "Förslag på betalningsplan",
  "pps-text-2": "Vi har skapat följande förslag på betalningsplan till dig. Du kan betala din skuld i flera rater enligt den. Närmare information om betalningsplan finns nedanför.",
  "pps-text-3": "Om du vill betala skulden enligt detta förslag, godkänna förslaget. Då träder betalningsplanen automatiskt i kraft.",
  "pps-text-4": "Är du säker på att du vill förkasta förslaget?",
  "pps-text-5": "Om du förkastar förslaget, sägs det upp omedelbart och fås inte i kraft på nytt. Indrivning av din skuld fortsätter som normalt.",
  "pps-text-6": "Visa detaljerad information",
  "pps-text-7": "Första raten",
  "pps-text-9": "Rater"

  },
  "en": {
  "pps-text-1": "Personal payment plan",
  "pps-text-2": "We have created a personal payment plan for you, which makes it possible to pay the debt in multiple installments. You can find the details of the plan below.",
  "pps-text-3": "If you wish to start paying your debt according to this plan, click Accept. The plan will be activated automatically.",
  "pps-text-4": "Are you sure you want to dismiss the payment plan?",
  "pps-text-5": "If you choose not to accept the personal payment plan, the collection of your case will continue normally.",
  "pps-text-6": "Show detailed information",
  "pps-text-7": "First installment",
  "pps-text-9": "Installments"
  }
  }
</i18n>
